import React, { useState, useEffect } from "react";
import styles from "./SelectEligibleProducts.module.css";
import { useFont } from "../../../contexts/FontContext/FontContext";
import BackButton from "../../BackButton/BackButton";
import EditExchangeForm from "./EditExchangeForm";
import ExchangesDefaultConfigForm from "./ExchangesDefaultConfigForm";

const SelectEligibleProducts = ( { exchangeOptionSelected, setExchangeOptionSelected } ) => {
	const apiUrl = process.env.REACT_APP_API_URL;
	const fontFamilyOne = useFont().scheme_one;
	const token = localStorage.getItem("authToken");
	const [activeProducts, setActiveProducts] = useState(null);
	const [exchangeProducts, setExchangeProducts] = useState(null);
	const [readyToUpdateSelectedExchangeProducts, setReadyToUpdateSelectedExchangeProducts] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [showLoader, setShowLoader] = useState(true);
	const [showSelectEligibleProducts, setShowSelectEligibleProducts] = useState(true);
	const [showExchangesDefaultConfigForm, setShowExchangesDefaultConfigForm] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);

	useEffect(() => { fetchActiveProducts(); }, []);
		
	// Fetch exchange products after active products are loaded
	useEffect(() => {
		if (activeProducts) { fetchExchangeProducts(); }
	}, [activeProducts]);

	//update state of exchange products in db
	useEffect(() => { 
		if (activeProducts && exchangeProducts && !readyToUpdateSelectedExchangeProducts) {
			if (Object.keys(activeProducts).length === 0) { alert("no active products on shopify!"); }
			else if (Object.keys(exchangeProducts).length === 0 && Object.keys(activeProducts).length !== 0) { updateExchangeProducts(activeProducts); } //no exchange products. Add one for each product in active products
			else if (Object.keys(exchangeProducts).length !== 0 && Object.keys(activeProducts).length !== 0) { 
	      		const unmatchedActiveProducts = activeProducts.filter(activeProduct => { return !exchangeProducts.some(exchangeProduct => activeProduct.id === exchangeProduct.product_id); }); //return slice of activeProducts that doesn't exist in the db
				if (unmatchedActiveProducts.length > 0) { updateExchangeProducts(unmatchedActiveProducts); } //only update if there are mismatches
		    }
		    setReadyToUpdateSelectedExchangeProducts(true);
		}
	}, [activeProducts, exchangeProducts]);

	//update exchange product selected toggle
	useEffect(() => {
		if (readyToUpdateSelectedExchangeProducts) { updateSelectedExchangeProducts(exchangeProducts); }
	}, [exchangeProducts])

	//organize data from shopify
	const serializeActiveProducts = (raw_products_list) => {
		return raw_products_list.map((node) => ({
		    id: node.id,
		    title: node.title,
		    image: node.images.edges[0]?.node?.src || "",
		    cost: node.variants.edges[0]?.node.price,
		    sku: node.variants.edges[0]?.node.sku
	  	}));
	};

	//get all active products
	const fetchActiveProducts = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/fetch_active_products/`, {
				method: "GET",
				headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
			});
			const data = await response.json();
			const serialized_data = serializeActiveProducts(data.products);
			setActiveProducts(serialized_data);
			setShowLoader(false);
		} catch (error) { console.error("Error fetching active products:", error); }
	}

	//get existing products
	const fetchExchangeProducts = async () => {
		try {
			const response = await fetch(`${apiUrl}/api/fetch_exchange_products/`, {
				method: "GET",
				headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
			});
			const data = await response.json();
			const sorted = data.exchange_products.sort((a, b) => {
				const titleA = a.product_title?.toLowerCase() || "";
				const titleB = b.product_title?.toLowerCase() || "";
				return titleA.localeCompare(titleB);
			});
			setExchangeProducts(sorted);
		} catch (error) { console.error("Error fetching existing exchange products:", error); }
	}

	//update exchange products in db (if there are active products that do not already exist as exchange products)
	const updateExchangeProducts = async (activeProducts) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_exchange_products/`, {
				method: "POST",
				headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
				body: JSON.stringify({ products: activeProducts }),
			});
			const data = await response.json();
			const sorted = data.exchange_products.sort((a, b) => {
				const titleA = a.product_title?.toLowerCase() || "";
				const titleB = b.product_title?.toLowerCase() || "";
				return titleA.localeCompare(titleB);
			});
			setExchangeProducts(sorted);
		} catch (error) { console.error("Error updating exchange products:", error); }
	}

	//update which exchange products are selected/unselected
	const updateSelectedExchangeProducts = async (exchangeProducts) => {
		try {
			const response = await fetch(`${apiUrl}/api/update_selected_exchange_products/`, {
				method: "POST",
				headers: {"Content-Type": "application/json", "Authorization": `Bearer ${token}`},
				body: JSON.stringify({ products: exchangeProducts }),
			});
			const data = await response.json();
		} catch (error) { console.error("Error updating selected exchange products:", error); }
	}

	//check and uncheck products
	const toggleProduct = (productId) => {
		setExchangeProducts((prev) => prev.map(product => product.product_id === productId ? { ...product, selected: !product.selected } : product) );		
	};

	//select and unselect all products at once
	const toggleAllProducts = (action) => {
		setExchangeProducts((prev) => prev.map(product => ({...product, selected: action === "select"})) );
	};

	return (
		<>
			{showSelectEligibleProducts && (
				<div className={`${styles.select_eligible_products_container} ${fontFamilyOne}`}>
					<p className={`titles ${styles.edit_select_eligible_products_title} fade_in`}>Select products eligible for exchange.</p>
					<div className={styles.select_eligible_products_box}>
						<div className={styles.search_products_container}>
							<div className={styles.search_bar_wrapper}>
								<input placeholder="Search products" className={styles.search_bar_input} onChange={(e) => {setSearchInput(e.target.value)}} />
							</div>
						</div>
						<div className={`${styles.products_list_container} ${showLoader ? "" : "fade_in"}`}>
						 	{showLoader && (<img className={styles.active_products_loader} src={`${process.env.PUBLIC_URL}/images/shipwheel_loader.gif`} />)}
							{!showLoader && exchangeProducts?.length > 0 && (
							    exchangeProducts.filter(p => searchInput.toLowerCase().split(" ").every(iw => p.product_title.toLowerCase().split(" ").some(tw => tw.startsWith(iw))) //filter search results for any that start with the prefix or contain words that start with the prefix
							    ).length === 0 ? (<p className={styles.no_results}>No results found.</p>)						
							    	: (exchangeProducts?.filter(p => searchInput.toLowerCase().split(" ").every(iw => p.product_title.toLowerCase().split(" ").some(tw => tw.startsWith(iw)))).map((product, index) => (			    	
							        	<div className={styles.product_wrapper} key={product.product_id}>
								        	<img className={styles.product_image} src={product.product_image} />
								        	<div className={styles.product_text_wrapper}>
								            	<p className={styles.product_text}>{product.product_title}</p>
								            </div>
								            <img className={`${styles.edit_icon} ${product.selected ? "" : styles.dim}`} src={`${process.env.PUBLIC_URL}/images/edit_icon.png`} alt="edit_icon" onClick={product.selected ? () => { setShowSelectEligibleProducts(false); setSelectedProduct(product); } : undefined} />
								            <img className={styles.product_check_mark} src={`${process.env.PUBLIC_URL}/images/${product.selected ? "checked_item.png" : "unchecked_item.png"}`} alt={product.selected ? "Checked" : "Unchecked"} onClick={() => toggleProduct(product.product_id)} />
								        </div>
							       ))
							    )
							)}
						</div>
						<div className={styles.filter_buttons_container}>
							<div className={styles.select_unselect_buttons} onClick={() => {toggleAllProducts("select")}}>Select all</div>
							<div className={styles.select_unselect_buttons} onClick={() => {toggleAllProducts("unselect")}}>Unselect all</div>
							<div className={styles.select_unselect_buttons} onClick={() => {setShowSelectEligibleProducts(false); setShowExchangesDefaultConfigForm(true);}}>Set defaults</div>
						</div>
					</div>
					<BackButton button_id={styles.select_eligible_products_back_button} image_id={styles.select_eligible_products_back_button_arrow} to={"home"} customBack={() => setExchangeOptionSelected(null) }/>
				</div>
			)}
			{!showSelectEligibleProducts && !showExchangesDefaultConfigForm && (<EditExchangeForm showSelectEligibleProducts={showSelectEligibleProducts} setShowSelectEligibleProducts={setShowSelectEligibleProducts} selectedProduct={selectedProduct} />)}
			{!showSelectEligibleProducts && showExchangesDefaultConfigForm && (<ExchangesDefaultConfigForm showSelectEligibleProducts={showSelectEligibleProducts} setShowSelectEligibleProducts={setShowSelectEligibleProducts} setShowExchangesDefaultConfigForm={setShowExchangesDefaultConfigForm} />)}
		</>
	);
};

export default SelectEligibleProducts;